import { CloseCircle, SearchNormal1 } from 'iconsax-react';
import { type ChangeEventHandler, type FocusEventHandler, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '@/utils/merge-class-names';
import { debounce } from 'lodash-es';

type Props = {
  keyword: string;
  setKeyword?: (keyword: string) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onSearch?: (keyword: string) => void;
  clearKeyword?: () => void;
  icon?: boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  inputClassName?: string;
  trigger?: 'enter' | 'button' | number;
  triggerButtonLabel?: string;
};

const SearchInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      keyword,
      placeholder,
      setKeyword,
      clearKeyword,
      onChange,
      onFocus,
      onBlur,
      onSearch,
      icon = true,
      className,
      autoFocus,
      inputClassName,
      trigger,
      triggerButtonLabel,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    return (
      <div className={cn(
        'flex items-center m-2',
        className,
      )}>
        <label
          className={cn(
            'input  flex h-10 min-w-0 items-center gap-2 border-none bg-base-100 text-sm has-[:focus]:border-none has-[:focus]:outline-none flex-1',
          )}>
          <div className='size-5'>
            <SearchNormal1 className={cn('size-5 text-secondary', { hidden: !icon })} />
          </div>
          <input
            ref={ref}
            type='text'
            id='search-input'
            autoFocus={autoFocus}
            className={cn('w-full grow bg-base-100', inputClassName)}
            value={keyword}
            enterKeyHint='search'
            placeholder={placeholder ?? t('search.placeholder')}
            onChange={(e) => {
              setKeyword?.(e.target.value);
              onChange?.(e);
              if (typeof trigger === 'number') {
                debounce(() => { onSearch?.(keyword) }, trigger);
              }
            }}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && trigger === 'enter') {
                onSearch?.(keyword);
              }
            }}
            autoComplete='off'
            autoCorrect='off'
            spellCheck='false'
            autoCapitalize='none'
          />
          {clearKeyword && keyword && (
            <button
              type='button'
              className='max-[320px]:hidden btn btn-circle btn-ghost btn-sm'
              onClick={clearKeyword}>
              <CloseCircle
                variant='Bold'
                className='size-5 text-secondary'
              />
            </button>
          )}
        </label>
        {
          trigger === 'button' && <span className='text-[#0a2fc0] mx-4' onClick={() => { onSearch?.(keyword) }}>{triggerButtonLabel ?? t('search.placeholder')}</span>
        }
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
